// components/Projects/EnhancedFileViewer.jsx
import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Editor from '@monaco-editor/react';
import { getFileIcon, getLanguageFromExtension } from '../../utils/helpers';
import { Edit2, Eye, Save, X, Copy } from 'lucide-react';

const EnhancedFileViewer = ({ files, onSaveChanges, isEditable = false }) => {
  const [selectedFile, setSelectedFile] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  if (!files || files.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-500">No files to display</p>
      </div>
    );
  }

  const currentFile = files[selectedFile];
  const currentContent = editedContent[currentFile.name] || currentFile.content;

  const getMonacoLanguage = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    const languageMap = {
      'js': 'javascript', 'jsx': 'javascript', 'ts': 'typescript', 'tsx': 'typescript',
      'py': 'python',
      'html': 'html', 'htm': 'html',
      'css': 'css', 'scss': 'scss', 'sass': 'sass',
      'json': 'json',
      'md': 'markdown',
      'txt': 'plaintext',
      'xml': 'xml',
      'yml': 'yaml', 'yaml': 'yaml',
      'java': 'java',
      'c': 'c', 'cpp': 'cpp', 'h': 'c',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust',
      'sql': 'sql',
      'sh': 'shell',
    };
    return languageMap[extension] || 'plaintext';
  };

  const handleSave = async () => {
    if (!onSaveChanges) return;

    setIsSaving(true);
    try {
      const changes = Object.entries(editedContent).map(([filename, content]) => ({
        name: filename,
        content: content
      }));

      await onSaveChanges(changes);
      setEditedContent({});
      setEditMode(false);
    } catch (error) {
      console.error('Failed to save changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditToggle = () => {
    if (editMode && Object.keys(editedContent).length > 0) {
      if (window.confirm('You have unsaved changes. Discard changes?')) {
        setEditedContent({});
        setEditMode(!editMode);
      }
    } else {
      setEditMode(!editMode);
    }
  };

  const handleContentChange = (value) => {
    setEditedContent(prev => ({
      ...prev,
      [currentFile.name]: value
    }));
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(currentContent);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xl">
      {/* Header with controls */}
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center overflow-x-auto">
          {files.map((file, index) => (
            <button
              key={index}
              onClick={() => setSelectedFile(index)}
              className={`flex items-center px-4 py-2 text-sm font-medium border-r border-gray-800 transition-all whitespace-nowrap ${
                index === selectedFile
                  ? 'bg-gray-800 text-white border-t-2 border-t-blue-500'
                  : 'bg-gray-900 text-gray-400 hover:text-gray-300 hover:bg-gray-800'
              }`}
            >
              <span className="mr-2">{getFileIcon(file.name)}</span>
              <span className="truncate max-w-xs">{file.name.split('/').pop()}</span>
              {editedContent[file.name] && (
                <span className="ml-2 text-xs text-yellow-500">●</span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-2">
          {/* Copy Button */}
          <button
            onClick={handleCopyToClipboard}
            className="flex items-center px-3 py-1.5 bg-gray-800 text-gray-300 rounded-md text-sm hover:bg-gray-700 transition-colors"
            title="Copy to clipboard"
          >
            <Copy className="w-4 h-4 mr-1" />
            Copy
          </button>

          {/* Edit/View Toggle */}
          {isEditable && (
            <button
              onClick={handleEditToggle}
              className={`flex items-center px-3 py-1.5 rounded-md text-sm transition-colors ${
                editMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {editMode ? (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  View Mode
                </>
              ) : (
                <>
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit Mode
                </>
              )}
            </button>
          )}

          {/* Save Changes Button */}
          {editMode && Object.keys(editedContent).length > 0 && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          )}

          {/* Cancel Edit Button */}
          {editMode && (
            <button
              onClick={() => {
                if (Object.keys(editedContent).length > 0) {
                  if (window.confirm('Discard all changes?')) {
                    setEditedContent({});
                    setEditMode(false);
                  }
                } else {
                  setEditMode(false);
                }
              }}
              className="flex items-center px-3 py-1.5 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 transition-colors"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="relative">
        {editMode ? (
          /* Monaco Editor for Edit Mode */
          <Editor
            height="500px"
            language={getMonacoLanguage(currentFile.name)}
            value={currentContent}
            theme="vs-dark"
            onChange={handleContentChange}
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              wordWrap: 'on',
              automaticLayout: true,
              scrollBeyondLastLine: false,
              renderLineHighlight: 'all',
              fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', monospace",
              bracketPairColorization: { enabled: true },
              guides: { bracketPairs: true },
              readOnly: false,
              contextmenu: true,
              formatOnPaste: true,
              formatOnType: true,
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnEnter: "on",
              tabCompletion: "on",
              wordBasedSuggestions: true
            }}
          />
        ) : (
          /* SyntaxHighlighter for View Mode */
          <SyntaxHighlighter
            language={getLanguageFromExtension(currentFile.name)}
            style={vscDarkPlus}
            showLineNumbers
            lineNumberStyle={{
              color: '#858585',
              minWidth: '40px',
              paddingRight: '12px'
            }}
            customStyle={{
              margin: 0,
              fontSize: '14px',
              lineHeight: '1.6',
              padding: '20px',
              backgroundColor: '#1e1e1e',
              fontFamily: "'Cascadia Code', 'Fira Code', 'Monaco', 'Consolas', monospace",
              minHeight: '500px'
            }}
          >
            {currentContent}
          </SyntaxHighlighter>
        )}

        {/* Unsaved Changes Indicator */}
        {editedContent[currentFile.name] && (
          <div className="absolute top-3 left-3 bg-yellow-500 text-black px-2 py-1 rounded text-xs font-medium animate-pulse">
            UNSAVED CHANGES
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-2 flex justify-between text-xs text-gray-400">
        <div className="flex items-center space-x-4">
          <span className="font-medium">
            {editMode ? 'EDITING' : 'VIEWING'}: {getLanguageFromExtension(currentFile.name).toUpperCase()}
          </span>
          <span>{currentContent.split('\n').length} lines</span>
          <span>{currentContent.length} characters</span>
          {editedContent[currentFile.name] && (
            <span className="text-yellow-500">● Modified</span>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <span>UTF-8</span>
          <span>LF</span>
          {editMode && <span className="text-green-500">Live Editing</span>}
        </div>
      </div>
    </div>
  );
};

export default EnhancedFileViewer;